"use client"

import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Table,
  Typography,
  type TableColumnsType,
  type TableProps,
} from "antd"
import type { SorterResult, TableRowSelection } from "antd/es/table/interface"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { LoaderSpinner } from "../../../components/common/Loader"
import {
  deleteRetractionRequest,
  getRequestTriplegs,
  getRetractionRequestLookups,
  saveApprovalRequestForm,
  saveRetractionRequestForm,
} from "../../../service/httpsCalls"
import { createTableConfig } from "../tableConfig"
// import RetractionCorrectDetails from "./retraction-correct-details"
import dayjs from "dayjs"
import "../billing.scss"
import RetractionRequestForm from "./RetractionRequestForm"

const { Text } = Typography

interface RetractionLookup {
  id: number
  name: string
  value?: string
  controlType?: string
}

interface PaginationObject {
  totalPages: number
  loading: boolean
  pageSize: number
  currentPage: number
  sortInfo: SorterResult<any>
  menuVisible: boolean
}

type NotificationType = "success" | "info" | "warning" | "error"

const { Option } = Select
const { confirm } = Modal
const { RangePicker } = DatePicker

const RetractionRequestGridHandler = ({ ratype }: any) => {
  const [searchByTypeOptions, setSearchByTypeOptions] = useState<RetractionLookup[]>([])
  const [searchByType, setSearchByType] = useState<any>(null)
  const [searchByTypeOption, setSearchByTypeOption] = useState<any>(null)
  const [tableColumns, setTableColumns] = useState<TableColumnsType<any>>([])
  const [notesOptions, setNotesOptions] = useState<RetractionLookup[]>([])
  const [note, setNote] = useState<any>(null)
  const [reasonOptions, setReasonOptions] = useState<RetractionLookup[]>([])
  const [reason, setReason] = useState<any>(null)
  const [approveOptions, setApproveOptions] = useState<RetractionLookup[]>([])
  const [approval, setApproval] = useState<any>(null)
  const [approvalNote, setApprovalNote] = useState<any>("")
  const [records, setRecords] = useState<any>([])
  const [form, setForm] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [masterRecords, setMasterRecords] = useState<any>([])
  const [submitted, setSubmitted] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [startDate, setStartDate] = useState<string | any>("")
  const [endDate, setEndDate] = useState<string | any>("")
  const [api, contextHolder] = notification.useNotification()
  const [tableHeight, setTableHeight] = useState(350)
  const [selectedTripLegs, setSelectedTripLegs] = useState<React.Key[]>([])
  const [filteredRowCount, setFilteredRowCount] = useState(0)
  const [tableKey, setTableKey] = useState(0) // Key state to refresh table
  const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight })

  const [showCorrectDetails, setShowCorrectDetails] = useState(false)

  const tblRef: Parameters<typeof Table>[0]["ref"] = useRef(null)
  const filtersState = useRef<any>(null)

  const selectedTripLegsRef = useRef<React.Key[]>([])

  const paginationObject: PaginationObject = {
    totalPages: 0,
    loading: false,
    pageSize: 10,
    currentPage: 1,
    sortInfo: {},
    menuVisible: false,
  }

  const [tablePagination, setTablePagination] = useState(paginationObject)

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const filteredValue = inputValue.replace(/[^a-zA-Z0-9,\-\s]/g, "")
    setSearchText(filteredValue)
  }

  const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
    api[type]({
      message: message,
      description: description,
    })
  }

  const getLookupData = () => {
    getRetractionRequestLookups(ratype, (res: any) => {
      if (res?.hasError) {
        const errorMsg =
          res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!"
        handleCallback({
          isInvalid: true,
          errorMessage: errorMsg,
        })
      } else {
        setSearchByTypeOptions(res.searchByTypes)
        setSearchByType(res.searchByTypes[0].value)
        setSearchByTypeOption(res.searchByTypes[0])
        // Set other lookup options
        setNotesOptions(res.notes || [])
        setReasonOptions(res.reasons || [])
        setApproveOptions(res.approveOptions || [])
      }
    })
  }

  function handleCallback(data: any) {
    if (data?.isInvalid) {
      openNotificationWithIcon("warning", data.errorMessage, "")
    } else if (data.isCancel) {
      // Handle cancel
    } else if (data.showMessage) {
      openNotificationWithIcon("success", data.showMessage, "")
    } else {
      openNotificationWithIcon("success", data.message, "")
    }
  }

  const handleDelete = (id: string) => {
    // Remove from records
    setRecords((prevRecords: any[]) => prevRecords.filter((record) => record.uniqueId !== id))

    // Remove from masterRecords
    setMasterRecords((prevMasterRecords: any[]) => prevMasterRecords.filter((record) => record.uniqueId !== id))

    // Remove from form
    setForm((prevForm: any[]) => prevForm.filter((item) => item.uniqueId !== id))

    // Remove from selectedTripLegs
    const currentSelectedTripLegs = selectedTripLegsRef.current    
    const updatedSelectedTripLegs = currentSelectedTripLegs.filter((key) => key !== id)
    setSelectedTripLegs(updatedSelectedTripLegs)

    // If no records are selected after deletion, hide the correct details panel
    if (updatedSelectedTripLegs.length === 0) {
      setShowCorrectDetails(false)
    }

    if(filtersState.current > 0){
      setFilteredRowCount(filtersState.current - 1)
      filtersState.current = filtersState.current - 1
    }
  }

  const renderDeleteButton = (text: any, record: any) => {
    if (record.canDelete) {
      return (
        <Button
          onClick={() => {
            const currentSelectedTripLegs = selectedTripLegsRef.current    
            confirm({
              title: currentSelectedTripLegs.includes(record.uniqueId)
                ? "This record has unsaved changes. Deleting it now will discard all changes."
                : "Do you want to delete this Retraction Request?",
              cancelText: "No",
              okText: "Yes",
              onOk() {
                deleteRetractionRequest(record.uniqueId, () => {
                  handleDelete(record.uniqueId)
                  handleCallback({
                    showMessage: "Retraction Request Deleted Successfully!",
                  })
                })
              },
              onCancel() {},
            })
          }}
          type="primary"
          size={screenSize.width < 768 ? "small" : "middle"}
        >
          Delete
        </Button>
      )
    } else return <></>
  }

  const handleSearch = () => {
    setSubmitted(true)
    resetChanges()
    if (
      searchByTypeOption.controlType === "DateRange"
        ? !(searchByType && startDate && endDate)
        : !(searchText && searchByType)
    ) {
      handleCallback({
        isInvalid: true,
        errorMessage: "Please fill required details.",
      })
      return
    }

    setTablePagination({
      ...tablePagination,
      loading: true,
    })
    setFilteredRowCount(0)
    filtersState.current = 0
    setMasterRecords([])
    setRecords([])
    setShowCorrectDetails(false)
    getRequestTriplegs(
      {
        requestActionType: Number.parseInt(ratype),
        searchByType: Number.parseInt(searchByType),
        tripCsv:
          searchByTypeOption.controlType === "DateRange"
            ? [startDate, endDate].map((item) => dayjs(item).format("YYYY-MM-DD")).join(",")
            : searchText,
      },
      (res: any) => {
        if (res?.hasError) {
          const errorMsg =
            res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!"
          handleCallback({
            isInvalid: true,
            errorMessage: errorMsg,
          })
          setRecords([])
          setMasterRecords([])
          setTablePagination({
            ...tablePagination,
            loading: false,
            totalPages: 1,
          })
        } else {
          setRecords(res.items || [])
          setMasterRecords(JSON.parse(JSON.stringify(res.items || [])))
          setTablePagination({
            ...tablePagination,
            loading: false,
            totalPages: Math.ceil((res.totalRecords || 0) / tablePagination.pageSize),
          })

          if (res.columnConfigs?.length) {
            const cols = createTableConfig(res.columnConfigs)
            const columns = [
              ...cols,
              {
                title: "",
                fixed: "right" as const,
                dataIndex: "uniqueId",
                width: screenSize.width < 768 ? 70 : 80,
                render: renderDeleteButton,
              },
            ]

            if(ratype === '1'){
              setTableColumns(columns);
            } else {
              setTableColumns(cols);
            }
          }
        }
      },
    )
  }

  const tableChanges: TableProps<any>["onChange"] = (pagination, filters, sorter, extra) => {
    if (extra.action == "filter") {
      setFilteredRowCount(extra.currentDataSource.length)
      filtersState.current = extra.currentDataSource.length
      return
    }
    if (extra.action == "sort") {
      return
    }
    setTablePagination({
      ...tablePagination,
      currentPage: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
      totalPages: pagination.total ?? 100,
      sortInfo: sorter as SorterResult<any>,
    })
  }

  const onSelectChange = (newSelectedTripKeys: React.Key[], selectedRows: any[]) => {
    setSelectedTripLegs(newSelectedTripKeys)
    // Show/hide correct details panel based on selection
    if (newSelectedTripKeys.length > 0) {
      setShowCorrectDetails(true)
    } else if (newSelectedTripKeys.length === 0) {
      resetRetractRequestForm()
      setShowCorrectDetails(false)
    }
  }

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys: selectedTripLegs,
    onChange: onSelectChange,
    fixed: "left",
    getCheckboxProps: (record: any) => ({
      disabled: !record.canSelect,
    }),
    preserveSelectedRowKeys: false, // Changed to false to fix the selection issue
  }

  const updateTableHeight = () => {
    const updateHeight = () => {
      const container = document.getElementById("retraction-table-container")
      if (container) {
        const header = container.getElementsByClassName("ant-table-header")[0]?.clientHeight ?? 10
        setTableHeight(container.clientHeight - header) // Added some padding
      }
    }

    setTimeout(updateHeight, 100) // Delay to ensure DOM is ready
    window.addEventListener("resize", updateHeight)

    return () => window.removeEventListener("resize", updateHeight)
  }

  // Handle screen resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({ width: window.innerWidth, height: window.innerHeight })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    getLookupData()
  }, [])



  useEffect(() => {
    if (records?.length) {
      updateTableHeight()
    }
  }, [records])

  useEffect(() => {
    selectedTripLegsRef.current = selectedTripLegs
  }, [selectedTripLegs])

  const handleRetractRequestForm = () => {
    if (ratype === "1") {
      setTablePagination({
        ...tablePagination,
        loading: true,
      })
      const newForm = form.filter((item) => !selectedTripLegs.includes(item.uniqueId))

      // Only include records that have actual changes
      const newList = selectedTripLegs
        .map((key) => {
          const existingRecord = records.find((rec: any) => rec.uniqueId === key)

          // Check if there are actual changes compared to existing values
          const hasReasonChange = existingRecord?.reasonId !== reason
          const hasNoteChange = existingRecord?.noteId !== note

          // Only return the form entry if there are changes
          if (hasReasonChange || hasNoteChange || existingRecord.detailsUpdated) {
            const formEntry: any = {
              uniqueId: key,
            }

            // Only add ReasonId if reason is not null
            if (reason !== null && reason !== undefined) {
              formEntry.ReasonId = reason
            } else if(existingRecord?.reasonId !== null) {
              formEntry.ReasonId = existingRecord?.reasonId
            } 

            // Only add NoteId if note is not null
            if (note !== null && note !== undefined) {
              formEntry.NoteId = note
            } else if(existingRecord?.reasonId !== null) {
              formEntry.NoteId = existingRecord?.noteId
            } 
            

            return formEntry
          }
          return null // No changes, exclude from form
        })
        .filter(Boolean) // Remove null entries
      setForm([...newForm, ...newList])

      // Apply changes to records with green highlighting
      const newRec = [...records]
      setRecords([])
      setTableKey((prevKey) => prevKey + 1) // Change table key to reset filters

      selectedTripLegs.forEach((uniqueId) => {
        const index = newRec.findIndex((rec) => rec.uniqueId == uniqueId)
        if (index != -1) {
          const oldData = newRec[index].oldData ?? newRec[index]

          // Get the display names for reason and note
          const reasonName = reasonOptions.find((r) => r.id === reason)?.name || ""
          const noteName = notesOptions.find((n) => n.id === note)?.name || ""

          const updatedRecord = {
            ...newRec[index],
            detailsUpdated: true,
            oldData: oldData,
          }

          // Only update reasonId and reason if reason is not null
          if (reason !== null && reason !== undefined) {
            updatedRecord.reasonId = reason
            updatedRecord.reason = reasonName
          }

          // Only update noteId and note if note is not null
          if (note !== null && note !== undefined) {
            updatedRecord.noteId = note
            updatedRecord.note = noteName
          }

          newRec[index] = updatedRecord
        }
      })
      setFilteredRowCount(0)
      filtersState.current = 0
      setRecords(newRec)
      setTimeout(() => {
        setTablePagination({
          ...tablePagination,
          loading: false,
        })
      }, 500)
    } else {
      setTablePagination({
        ...tablePagination,
        loading: true,
      })
      const newForm = form.filter((item) => !selectedTripLegs.includes(item.uniqueId))
      // Only include records that have actual changes
      const newList = selectedTripLegs
        .map((key) => {
          const existingRecord = records.find((rec: any) => rec.uniqueId === key)

          // Check if there are actual changes compared to existing values
          const hasStatusChange = existingRecord?.statusId !== approval
          const hasNoteChange = existingRecord?.statusNote !== approvalNote

          // Only return the form entry if there are changes
          if (hasStatusChange || hasNoteChange || existingRecord.detailsUpdated) {
            const formEntry: any = {
              uniqueId: key,
            }

            // Only add StatusId if approval is not null
            if (approval !== null && approval !== undefined) {
              formEntry.StatusId = approval
            } else if(existingRecord?.statusId !== null) {
              formEntry.statusId = existingRecord?.statusId
            } 

            // Only add StatusNote if approvalNote is not null or empty
            if (approvalNote !== null && approvalNote !== undefined && approvalNote.trim() !== "") {
              formEntry.StatusNote = approvalNote
            } else if(existingRecord?.statusNote !== null) {
              formEntry.statusNote = existingRecord?.statusNote
            } 

            return formEntry
          }
          return null // No changes, exclude from form
        })
        .filter(Boolean) // Remove null entries

      setForm([...newForm, ...newList])

      // Apply changes to records with green highlighting
      const newRec = [...records]
      setRecords([])
      setTableKey((prevKey) => prevKey + 1) // Change table key to reset filters

      selectedTripLegs.forEach((uniqueId) => {
        const index = newRec.findIndex((rec) => rec.uniqueId == uniqueId)
        if (index != -1) {
          const oldData = newRec[index].oldData ?? newRec[index]

          // Get the display names for status
          const statusName = approveOptions.find((r) => r.id === approval)?.name || ""

          const updatedRecord = {
            ...newRec[index],
            detailsUpdated: true,
            oldData: oldData,
          }

          // Only update statusId and status if approval is not null
          if (approval !== null && approval !== undefined) {
            updatedRecord.statusId = approval
            updatedRecord.status = statusName
          }

          // Only update statusNote if approvalNote is not null or empty
          if (approvalNote !== null && approvalNote !== undefined && approvalNote.trim() !== "") {
            updatedRecord.statusNote = approvalNote
          }

          newRec[index] = updatedRecord
        }
      })
      setFilteredRowCount(0)
      filtersState.current = 0
      setRecords(newRec)
      setTimeout(() => {
        setTablePagination({
          ...tablePagination,
          loading: false,
        })
      }, 300)
    }
  }

  const resetRetractRequestForm = () => {
    setNote(null)
    setReason(null)
    setApproval(null)
    setApprovalNote("")
  }

  const handleCancelAllChanges = () => {
    confirm({
      title: "Do you want to discard unsaved changes?",
      cancelText: "No",
      okText: "Yes",
      onOk() {
        resetChanges()
      },
      onCancel() {},
    })
  }

  const resetChanges = () => {
    resetRetractRequestForm()
    setShowCorrectDetails(false)
    setSelectedTripLegs([])
    setForm([])
    setFilteredRowCount(0)
    filtersState.current = 0
    setTableKey((prevKey) => prevKey + 1)
    setRecords(masterRecords)
  }


  const saveChanges = () => {
    setLoading(true)
    if (form.length) {
      confirm({
        title: "Do you want to save these changes?",
        cancelText: "No",
        okText: "Yes",
        onOk() {
          ratype === "1"
            ? saveRetractionRequestForm({ Triplegs: form }, (res: any) => {
                setLoading(false)
                if (res?.hasError) {
                  const errorMsg =
                    res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!"
                  handleCallback({
                    isInvalid: true,
                    errorMessage: errorMsg,
                  })
                } else {
                  handleCallback({
                    success: true,
                    message: "Retraction Requests saved successfully!",
                  })
                  resetChanges()
                  setRecords([])
                  handleSearch()
                }
              })
            : saveApprovalRequestForm({ Triplegs: form }, (res: any) => {
                setLoading(false)
                if (res?.hasError) {
                  const errorMsg =
                    res?.error?.messages?.length > 0 ? res.error?.messages[0].description : "Something went wrong!!!"
                  handleCallback({
                    isInvalid: true,
                    errorMessage: errorMsg,
                  })
                } else {
                  handleCallback({
                    success: true,
                    message: "Retraction Requests saved successfully!",
                  })
                  resetChanges()
                  setRecords([])
                  handleSearch()
                }
              })
        },
        onCancel() {
          setLoading(false);
        },
      })
    } else {
      setLoading(false)
      handleCallback({
        isInvalid: true,
        errorMessage: "Please Select one of the invoices to save changes.",
      })
    }  
  }

  useEffect(() => {
    if (submitted && tablePagination.pageSize != 0) handleSearch()
  }, [tablePagination.currentPage, tablePagination.pageSize, tablePagination.sortInfo])

  return (
    <>
      {contextHolder}
      <div className={records?.length > 0 ? "parentDiv" : ""}>
        <div style={{ width: records?.length > 0 && !selectedTripLegs.length ? "100%" : "" }}>
          <div style={{ background: "#fff", padding: screenSize.width < 768 ? "10px" : "15px" }}>
            <Row
              style={{
                paddingBottom: "0.5rem",
                paddingTop: "0.5rem",
              }}
            >
              <Col span={24}>
                <div style={{ width: "100%" }}>
                  <Select
                    style={{ width: "200px" }}
                    onChange={(value) => {
                      setSearchByType(value)
                      setSearchByTypeOption(searchByTypeOptions.find((x) => x.value == value))
                      setSearchText("")
                      setStartDate(null)
                      setEndDate(null)
                    }}
                    disabled={form?.length > 0}
                    value={searchByType}
                  >
                    {searchByTypeOptions &&
                      searchByTypeOptions.map((item: any) => <Option key={item.value}>{item.name}</Option>)}
                  </Select>
                  {searchByTypeOption?.controlType === "TextBox" && (
                    <Input
                      style={{ width: "calc(100% - 200px)" }}
                      allowClear
                      status={submitted && !searchText ? "error" : ""}
                      placeholder={
                        "Enter Comma/Space separated value" +
                        (searchByTypeOptions?.length && searchByTypeOption ? " of " + searchByTypeOption?.name : "")
                      }
                      disabled={form?.length > 0}
                      value={searchText}
                      onChange={handleSearchTextChange}
                    />
                  )}
                  {searchByTypeOption?.controlType == "DateRange" && (
                    <RangePicker
                      style={{ width: "calc(100% - 200px)" }}
                      allowClear
                      disabled={form?.length > 0}
                      status={submitted && !startDate && !endDate ? "error" : ""}
                      name="SearchDateRangePicker"
                      placeholder={["From Date", "To Date"]}
                      format="MM/DD/YYYY"
                      onChange={(dates: any) => {
                        if (dates) {
                          setStartDate(dates[0])
                          setEndDate(dates[1])
                        } else {
                          setStartDate("")
                          setEndDate("")
                        }
                      }}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <Flex
              justify="end"
              style={{ paddingBottom: "0.5rem", paddingTop: "0.5rem", alignItems: "center", background: "#fff" }}
              gap="small"
            >
              <Button
                onClick={handleSearch}
                loading={tablePagination.loading}
                style={{ marginLeft: "5px" }}
                type="primary"
                disabled={form?.length > 0}
                size={screenSize.width < 768 ? "small" : "middle"}
              >
                Search
              </Button>
            </Flex>
          </div>

          <Card
            styles={{
              body: {
                padding: screenSize.width < 768 ? "10px" : "15px",
                paddingTop: "10px",
                height: "auto",
                maxHeight: records?.length > 0 ? "calc(100vh - 268px)" : undefined,
              },
            }}
          >
            {records?.length > 0 && (
              <Flex
                justify="start"
                style={{ paddingBottom: "5px", paddingTop: "0", alignItems: "center", background: "#fff" }}
                gap="small"
              >
                <div
                  style={{
                    background: "#e6f4ff",
                    padding: "5px",
                    fontWeight: 500,
                    fontSize: screenSize.width < 768 ? "12px" : "14px",
                  }}
                >
                  <span
                    style={{
                      marginRight: "5px",
                      marginLeft: "5px",
                      borderRight: "1px solid #d4d2d2",
                      paddingRight: "5px",
                    }}
                  >
                    Total: {records.length}
                  </span>
                  <span style={{ marginRight: "5px" }}>Selected: {selectedTripLegs.length}</span>
                  <span style={{ marginRight: "5px" }}>Modified: {form.length}</span>
                  {filteredRowCount > 0 && filteredRowCount != records.length && (
                    <span style={{ marginRight: "5px", borderLeft: "1px solid #d4d2d2", paddingLeft: "5px" }}>
                      Filtered: {filteredRowCount}
                    </span>
                  )}
                </div>
              </Flex>
            )}

            {records?.length > 0 && (
              <div
                id="retraction-table-container"
                style={{
                  height: "calc(100vh - 400px)",
                  width: showCorrectDetails
                    ? screenSize.width < 768
                      ? "calc(100vw - 300px)"
                      : "calc(100vw - 650px)"
                    : screenSize.width < 768
                      ? "calc(100vw - 40px)"
                      : "calc(100vw - 380px)",
                  overflow: "hidden",
                  marginRight: showCorrectDetails ? "20px" : "5px",
                }}
              >
                <Table
                  scroll={{
                    y: tableHeight,
                    x: tableColumns.reduce((acc, col: any) => acc + (col.width || 150), 0) + 25,
                  }}
                  key={tableKey}
                  rowKey={"uniqueId"}
                  ref={tblRef}
                  virtual={true}
                  rowSelection={rowSelection}
                  dataSource={records}
                  pagination={false}
                  columns={tableColumns}
                  onChange={tableChanges}
                  size={screenSize.width < 768 ? "small" : "small"}
                  loading={{
                    indicator: <LoaderSpinner size={24} topMargin="10px" />,
                    spinning: tablePagination.loading,
                  }}
                  onHeaderRow={(columns, index) => {
                    return {
                      onContextMenu: (event) => {
                        event.preventDefault()
                        if (!tablePagination.menuVisible) {
                          document.addEventListener(`click`, function onClickOutside() {
                            setTablePagination({
                              ...tablePagination,
                              menuVisible: false,
                            })
                            document.removeEventListener(`click`, onClickOutside)
                          })
                        }
                        setTablePagination({
                          ...tablePagination,
                          menuVisible: true,
                        })
                      },
                    }
                  }}
                />
              </div>
            )}

            {records?.length > 0 && (
              <Flex justify="end" style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #f0f0f0" }}>
                <Button
                  onClick={saveChanges}
                  type="primary"
                  loading={loading}
                  disabled={records.length === 0 || form.length === 0}
                  size={screenSize.width < 768 ? "small" : "middle"}
                >
                  Save
                </Button>
                <Button
                  onClick={handleCancelAllChanges}
                  style={{ marginLeft: "8px" }}
                  disabled={records.length === 0 || form.length === 0}
                  size={screenSize.width < 768 ? "small" : "middle"}
                >
                  Cancel
                </Button>
              </Flex>
            )}
          </Card>
        </div>
        {showCorrectDetails && selectedTripLegs.length > 0 && (
          <div
            style={{
              width: screenSize.width < 768 ? "200px" : "250px",
              background: "#fff",
              margin: "0",
              paddingLeft: "5px",
              border: "1px solid lightgray",
            }}
          >
            <RetractionRequestForm
              note={note}
              ratype={ratype}
              setNote={setNote}
              notesOptions={notesOptions}
              reason={reason}
              approvalNote={approvalNote}
              setApprovalNote={setApprovalNote}
              setReason={setReason}
              reasonOptions={reasonOptions}
              approval={approval}
              setApproval={setApproval}
              handleRetractRequestForm={handleRetractRequestForm}
              cancelRetractRequestForm={resetRetractRequestForm}
              approveOptions={approveOptions}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default RetractionRequestGridHandler
